import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { User, Globe, CreditCard, Crown, CheckCircle, Loader2, Lock, Eye, EyeOff, HelpCircle, ExternalLink, Calendar, AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SettingsPage() {
  const { t, language, setLanguage } = useI18n();
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [saving, setSaving] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [recipeCount, setRecipeCount] = useState(0);
  const [menuCount, setMenuCount] = useState(0);
  const [config, setConfig] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);

  useEffect(() => {
    fetchCounts();
    fetchConfig();

    if (user?.plan !== 'free') {
      fetchSubscription();
    }

    // Check for payment session
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams, user?.plan]);

  const fetchCounts = async () => {
    try {
      const [recipesRes, menusRes] = await Promise.all([
        fetch(`${API}/recipes`, { credentials: 'include' }),
        fetch(`${API}/menus`, { credentials: 'include' }),
      ]);

      if (recipesRes.ok) {
        const recipes = await recipesRes.json();
        setRecipeCount(recipes.length);
      }
      if (menusRes.ok) {
        const menus = await menusRes.json();
        setMenuCount(menus.length);
      }
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API}/config/public`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      } else {
        // Fallback defaults if API fails
        setConfig({
          free_max_recipes: 20,
          free_max_menus: 5,
        });
      }
    } catch (error) {
      setConfig({
        free_max_recipes: 20,
        free_max_menus: 5,
      });
    }
  };

  const fetchSubscription = async () => {
    setLoadingSubscription(true);
    try {
      const response = await fetch(`${API}/payments/subscription`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.status !== 'none') {
          setSubscription(data);
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      try {
        const response = await fetch(`${API}/payments/status/${sessionId}`, {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();

          if (data.payment_status === 'paid') {
            toast.success('¡Pago completado! Ahora eres Premium');
            // Clear session_id from URL
            navigate('/settings', { replace: true });
            // Refresh page to get updated user data
            window.location.reload();
            return;
          } else if (data.status === 'expired') {
            toast.error('La sesión de pago expiró');
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          toast.error('No se pudo verificar el pago. Contacta soporte.');
        }
      } catch (error) {
        console.error('Payment status check error:', error);
      } finally {
        if (attempts >= maxAttempts) {
          setCheckingPayment(false);
        }
      }
    };

    poll();
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const result = await updateProfile({ name, email, language });
      if (result) {
        toast.success(t('success.saved'));
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [requestingCode, setRequestingCode] = useState(false);

  const initiateEmailChange = async () => {
    // If email hasn't changed, just save normally
    if (email === user?.email) {
      handleSaveProfile();
      return;
    }

    setRequestingCode(true);
    try {
      const response = await fetch(`${API}/auth/request-email-change-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ new_email: email }),
      });

      if (response.ok) {
        setShowVerificationModal(true);
        toast.success(t('auth.verificationCodeSent') || "Código de verificación enviado");
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setRequestingCode(false);
    }
  };

  const confirmEmailChange = async () => {
    setSaving(true);
    try {
      const result = await updateProfile({ name, email, language, verification_code: verificationCode });
      if (result) {
        toast.success(t('success.saved'));
        setShowVerificationModal(false);
        setVerificationCode('');
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const handleLanguageChange = async (newLang) => {
    setLanguage(newLang);
    await updateProfile({ language: newLang });
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      toast.error(t('settings.passwordTooShort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('settings.passwordMismatch'));
      return;
    }

    setChangingPassword(true);
    try {
      const response = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      if (response.ok) {
        toast.success(t('settings.passwordChanged'));
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const error = await response.json();
        if (response.status === 401) {
          toast.error(t('settings.passwordWrong'));
        } else {
          toast.error(error.detail || t('errors.generic'));
        }
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleRepeatTutorial = async () => {
    try {
      await updateProfile({
        has_seen_tutorial: false,
        tutorial_pages_seen: []
      });
      toast.success(language === 'es' ? 'Tutorial reiniciado. Redirigiendo...' : 'Tutorial reset. Redirecting...');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    try {
      const response = await fetch(`${API}/payments/create-portal-session`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Portal error:', error);
      toast.error(t('errors.generic'));
    } finally {
      setManagingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('¿Seguro que quieres cancelar tu suscripción? Seguirás teniendo acceso hasta el final del periodo actual.')) {
      return;
    }

    setCancelingSubscription(true);
    try {
      const response = await fetch(`${API}/payments/cancel-subscription`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Suscripción cancelada correctamente');
        fetchSubscription(); // Refresh data
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(t('errors.generic'));
    } finally {
      setCancelingSubscription(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isPremium = user?.plan === 'premium' || user?.plan === 'pro';

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-2xl mx-auto">
        {/* Header */}
        <h1 className="font-manrope font-bold text-3xl text-charcoal mb-8">
          {t('settings.title')}
        </h1>

        {/* Payment Status Check */}
        {checkingPayment && (
          <Card className="bg-primary/10 border-primary/20 rounded-2xl p-4 mb-6 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-primary animate-spin" />
            <p className="font-inter text-charcoal">Verificando estado del pago...</p>
          </Card>
        )}

        {/* Profile Section */}
        <Card className="bg-white rounded-3xl shadow-soft p-6 mb-6" data-testid="profile-settings-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <h2 className="font-manrope font-semibold text-xl text-charcoal">
              {t('settings.profile')}
            </h2>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="font-manrope font-semibold text-charcoal">
                Nombre
              </Label>
              <Input
                data-testid="profile-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 rounded-xl border-border bg-white/50 font-inter"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-manrope font-semibold text-charcoal">
                Email
              </Label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl border-border bg-white/50 font-inter"
              />
            </div>

            <Button
              onClick={initiateEmailChange}
              disabled={saving || requestingCode}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-5 font-manrope font-bold"
              data-testid="save-profile-button"
            >
              {saving || requestingCode ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {t('save')}
            </Button>
          </div>
        </Card>

        <Dialog open={showVerificationModal} onOpenChange={setShowVerificationModal}>
          <DialogContent className="sm:max-w-md bg-white rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl text-charcoal flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                Verificación de Seguridad
              </DialogTitle>
              <DialogDescription className="font-inter text-stone">
                Para cambiar tu email a <b>{email}</b>, introduce el código que acabamos de enviar a tu dirección actual ({user?.email}).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Código de confirmación</Label>
                <Input
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
                  placeholder="Ej: A1B2C3"
                  className="font-mono text-center text-lg tracking-widest uppercase"
                  maxLength={6}
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:justify-end gap-2">
              <Button variant="outline" onClick={() => setShowVerificationModal(false)} className="rounded-full">Cancelar</Button>
              <Button onClick={confirmEmailChange} disabled={!verificationCode || saving} className="bg-primary text-primary-foreground rounded-full">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Confirmar Cambio
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Language Section */}
        <Card className="bg-white rounded-3xl shadow-soft p-6 mb-6" data-testid="language-settings-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-sage/10 rounded-full flex items-center justify-center">
              <Globe className="w-5 h-5 text-sage" strokeWidth={1.5} />
            </div>
            <h2 className="font-manrope font-semibold text-xl text-charcoal">
              {t('settings.language')}
            </h2>
          </div>

          <Select value={language} onValueChange={handleLanguageChange}>
            <SelectTrigger
              className="h-12 rounded-xl border-border bg-white/50 font-inter"
              data-testid="language-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="en">English</SelectItem>
            </SelectContent>
          </Select>
        </Card>

        {/* Tutorial Support Section */}
        <Card className="bg-white rounded-3xl shadow-soft p-6 mb-6" data-testid="tutorial-settings-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <h2 className="font-manrope font-semibold text-xl text-charcoal">
              {language === 'es' ? 'Ayuda y Tutorial' : 'Help & Tutorial'}
            </h2>
          </div>
          <p className="font-inter text-stone mb-6">
            {language === 'es'
              ? '¿Necesitas recordar cómo funciona la aplicación? Puedes volver a ver la guía de bienvenida.'
              : 'Need a reminder on how the app works? You can watch the welcome guide again.'}
          </p>
          <Button
            onClick={handleRepeatTutorial}
            variant="outline"
            className="border-primary text-primary hover:bg-primary/5 rounded-full px-6 py-5 font-manrope font-bold"
            data-testid="repeat-tutorial-button"
          >
            {language === 'es' ? 'Repetir Tutorial' : 'Repeat Tutorial'}
          </Button>
        </Card>

        {/* Security Section - Only for email auth users */}
        {user?.auth_type === 'email' && (
          <Card className="bg-white rounded-3xl shadow-soft p-6 mb-6" data-testid="security-settings-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-amber-500/10 rounded-full flex items-center justify-center">
                <Lock className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
              </div>
              <h2 className="font-manrope font-semibold text-xl text-charcoal">
                {t('settings.security')}
              </h2>
            </div>
            {/* Password change form... (skipped for brevity as it was unchanged) */}
            <div className="space-y-4">
              <div>
                <Label className="font-manrope font-semibold text-charcoal mb-2 block">
                  {t('settings.currentPassword')}
                </Label>
                <div className="relative">
                  <Input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="h-12 rounded-xl border-border bg-white/50 font-inter pr-12"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.5} /> : <Eye className="w-5 h-5" strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="font-manrope font-semibold text-charcoal mb-2 block">
                  {t('settings.newPassword')}
                </Label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="h-12 rounded-xl border-border bg-white/50 font-inter pr-12"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.5} /> : <Eye className="w-5 h-5" strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="font-manrope font-semibold text-charcoal mb-2 block">
                  {t('settings.confirmPassword')}
                </Label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-12 rounded-xl border-border bg-white/50 font-inter pr-12"
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.5} /> : <Eye className="w-5 h-5" strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              <Button
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-5 font-manrope font-bold mt-2"
                data-testid="change-password-button"
              >
                {changingPassword ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Lock className="w-4 h-4 mr-2" strokeWidth={1.5} />
                )}
                {t('settings.changePassword')}
              </Button>
            </div>
          </Card>
        )}

        {/* Subscription Section */}
        <Card className="bg-white rounded-3xl shadow-soft p-6" data-testid="subscription-settings-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <h2 className="font-manrope font-semibold text-xl text-charcoal">
              {t('settings.subscription')}
            </h2>
          </div>

          {/* Current Plan */}
          <div className={`flex items-center justify-between p-4 rounded-2xl mb-6 ${isPremium ? 'bg-primary/5 border border-primary/10' : 'bg-muted/50'}`}>
            <div className="flex items-center gap-3">
              {isPremium ? (
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                  <Crown className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
              ) : (
                <div className="w-12 h-12 bg-sage/10 rounded-full flex items-center justify-center shrink-0">
                  <CheckCircle className="w-6 h-6 text-sage" strokeWidth={1.5} />
                </div>
              )}
              <div>
                <p className="font-manrope font-bold text-lg text-charcoal">
                  {user?.plan === 'premium' ? 'Plan Premium' : user?.plan === 'pro' ? 'Plan Pro' : t('settings.free')}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${user?.subscription_status === 'active' ? 'bg-green-100 text-green-700' :
                    user?.subscription_status === 'canceling_at_period_end' ? 'bg-amber-100 text-amber-700' :
                      'bg-stone/10 text-stone'
                    }`}>
                    {user?.subscription_status === 'active' ? t('settings.active') :
                      user?.subscription_status === 'canceling_at_period_end' ? 'Cancelado (fin de periodo)' :
                        user?.plan === 'free' ? 'Activo' : user?.subscription_status}
                  </span>
                  {isPremium && (
                    <span className="text-xs text-stone font-inter">
                      • {isPremium ? t('settings.unlimited') : 'Plan básico'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Details (Only for Premium/Pro) */}
          {isPremium && subscription && (
            <div className="mb-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted/30 rounded-2xl">
                  <p className="text-stone text-xs font-inter mb-1 uppercase tracking-wider">{t('settings.amount')}</p>
                  <p className="font-manrope font-bold text-lg text-charcoal">
                    £{subscription.plan_amount} <span className="text-sm font-normal text-stone">/{subscription.interval}</span>
                  </p>
                </div>
                <div className="p-4 bg-muted/30 rounded-2xl">
                  <p className="text-stone text-xs font-inter mb-1 uppercase tracking-wider">
                    {subscription.cancel_at_period_end ? t('settings.canceling_at_period_end') : t('settings.nextBilling')}
                  </p>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    <p className="font-manrope font-bold text-lg text-charcoal">
                      {formatDate(subscription.current_period_end)}
                    </p>
                  </div>
                </div>
              </div>

              {subscription.cancel_at_period_end && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800 text-sm">Suscripción cancelada</p>
                    <p className="text-amber-700 text-sm mt-1">
                      Tu suscripción finalizará el {formatDate(subscription.current_period_end)}. Hasta entonces puedes seguir disfrutando de todas las ventajas.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Usage Stats (Only for Free) */}
          {!isPremium && config && (
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center">
                <span className="font-inter text-stone">
                  {t('settings.limits.recipes')}
                </span>
                <span className="font-manrope font-semibold text-charcoal">
                  {recipeCount} {t('settings.limits.of')} {config.free_max_recipes}
                </span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${Math.min((recipeCount / config.free_max_recipes) * 100, 100)}%` }}
                />
              </div>

              <div className="flex justify-between items-center mt-4">
                <span className="font-inter text-stone">
                  {t('settings.limits.menus')}
                </span>
                <span className="font-manrope font-semibold text-charcoal">
                  {menuCount} {t('settings.limits.of')} {config.free_max_menus}
                </span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-sage rounded-full transition-all"
                  style={{ width: `${Math.min((menuCount / config.free_max_menus) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            {!isPremium ? (
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full py-6 font-manrope font-bold shadow-lg"
                data-testid="upgrade-button"
              >
                <Crown className="w-5 h-5 mr-2" strokeWidth={1.5} />
                {t('settings.upgrade')}
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleManageSubscription}
                  disabled={managingSubscription}
                  variant="outline"
                  className="w-full border-primary text-primary hover:bg-primary/5 rounded-full py-4 font-manrope font-bold h-auto"
                >
                  <span className="flex items-center justify-center gap-2 flex-wrap">
                    {managingSubscription ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                    <span className="text-center leading-tight">
                      {t('settings.manageSubscription')}                     
                    </span>
                  </span>
                </Button>

                <Button
                  onClick={() => navigate('/pricing')}
                  variant="outline"
                  className="w-full border-primary text-primary hover:bg-primary/5 rounded-full py-6 font-manrope font-bold"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Ver Planes y Precios' : 'View Plans & Pricing'}
                </Button>

                {!subscription?.cancel_at_period_end && (
                  <Button
                    onClick={handleCancelSubscription}
                    disabled={cancelingSubscription}
                    variant="ghost"
                    className="w-full text-stone hover:text-red-500 hover:bg-red-50 rounded-full py-4 font-inter text-sm"
                  >
                    {cancelingSubscription ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : null}
                    {t('settings.cancelSubscription')}
                  </Button>
                )}
              </>
            )}
          </div>

          {isPremium && !subscription && !loadingSubscription && (
            <p className="text-center text-xs text-stone mt-4">
              ¿No ves los detalles de tu suscripción? <button onClick={fetchSubscription} className="text-primary underline">Recargar</button>
            </p>
          )}
        </Card>
      </div>
    </Layout>
  );
}
